<template>
    <wrapper-features-templates-one-column
    :hideWhiteBoxOnLoad="showLoader || (!tableData.length && !showLoader && !applyErrorState)"
    :showLoader="showLoader"
	:showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState"
    @backOrCloseClicked="navigateByName(routeList.USER_AREA_DEFINITIONS_NAME)">
        <transition name="common-enter-animation" mode="out-in" appear>
            <table-generic
            v-if="tableData.length && !applyErrorState"
            noHoverStyles
            hideDetailButton
            :headerData="tableHeaderData"
            :tableData="tableData"
            :tableStyles="tableStyles"
            @toggleSwitch="[switchClickHandler($event), parseToSelectRowSwitchHandler()]"/>
        </transition>
    </wrapper-features-templates-one-column>
    <!-- Confirm removing access to channel modal -->
    <modal-centered-generic
    v-if="showCenteredModal"
    negative
    :title="modalParams.title"
    :text="modalParams.text"
    :rightButtonText="modalParams.rightButtonText"
    rightButtonIcon="check-line"
    @rightButtonClicked="confirmChannelUpdateHandler"
    @hideMe="[showCenteredModal = false, parseToSelectRowSwitchHandler()]"/>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import ToastGeneric from '@/components/generic/toasts/Generic'
import TableGeneric from '@/components/generic/table/Generic'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'

/* Vue and Assets */
import { ref, onBeforeUnmount } from 'vue'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { getDateWithTime } from '@/assets/scripts/dateBuilders'
import { channelIconMapper } from '@/assets/scripts/mappers/channels'
import { parseToSelectRowSwitch } from '@/assets/scripts/parsers/tables'
import { errorObjectConstructor, emptyObjectConstructor, sucessObjectConstrutor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { requestChannelsList, updateChannel, clearFeatureStates } = mapActions(moduleMapper.USER_AREA_DEFINITIONS_ACTIVE_CHANNELS)
const { getChannelsList } = mapGetters(moduleMapper.USER_AREA_DEFINITIONS_ACTIVE_CHANNELS)
const { getMobileAppState } = mapGetters()
const { forceLogout } = mapActions(moduleMapper.SESSION)

/* Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const applyErrorState = ref(false)
const showCenteredModal = ref(false)
const modalPayload = ref(null)
const modalParams = ref({})
const eventRowId = ref(null)
const tableData = ref([])

/************************************************
*                                               *
*                  Table Data                   *
*                                               *
*************************************************/
const tableHeaderData = [
    { label: t('userArea.definitions.activeChannels.tableHeaders.channel'), showInMobile: true, dataAttr: 'category', rowCellType: 'category' },
    { label: '', dataAttr: 'description', showInMobile: { groupWith: 'lastChangeDate' }, rowCellType: 'title' },
    { label: t('userArea.definitions.activeChannels.tableHeaders.lastUpdate'), dataAttr: 'lastChangeDate' },
    { label: t('userArea.definitions.activeChannels.tableHeaders.status'), showInMobile: true, dataAttr: 'status', rowCellType: 'switch' }
]

const tableStyles = [
    null,
    { width: '170px', 'min-width': '170px', 'text-align': 'left' },
    { 'text-align': 'end', 'min-width': '80px' },
    null
]

const setTableData = () => {
    tableData.value = getChannelsList.value.map(channel => {
        return [
            { value: channel.id, dataAttr: 'id' },
            { type: 'category', value: { backgroundColor: 'var(--primary)', icon: channelIconMapper(channel.id) }, dataAttr: 'category' },
            { type: 'title', value: channel.description, dataAttr: 'description' },
            { value: { content: getDateWithTime(channel.lastChangeDate), classes: 'capitalize' }, dataAttr: 'lastChangeDate' },
            { type: 'switch', value: channel.active, dataAttr: 'status' }
        ]
    })
}

const switchClickHandler = ev => {
    eventRowId.value = ev.rowId
    const targetChannel = getChannelsList.value.find(channel => channel.id === ev.rowId)
    modalPayload.value = {
        id: targetChannel.id,
        state: !ev.isActive
    }

    if (ev.isActive) {
        modalParams.value = {
            title: t('userArea.definitions.activeChannels.confirmationModal.title'),
            text: t('userArea.definitions.activeChannels.confirmationModal.text', { channelDescription: targetChannel.description }),
            rightButtonText: t('userArea.definitions.activeChannels.confirmationModal.rightButtonText')
        }
        showCenteredModal.value = true
        return
    }

    confirmChannelUpdateHandler()
}

const parseToSelectRowSwitchHandler = () => {
	tableData.value = parseToSelectRowSwitch(tableData.value, eventRowId.value)
}

/************************************************
*                                               *
*               Requests Logic                  *
*                                               *
*************************************************/
const initialRequests = async () => {
    showLoader.value = true
    applyErrorState.value = false

    try {
        await requestChannelsList()
        setTableData()
        showLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
    }
}

const confirmChannelUpdateHandler = async () => {
    showOverlayLoader.value = true
    applyErrorState.value = false
    showCenteredModal.value = false

    try {
        const res = await updateChannel(modalPayload.value)
        setTableData()
        showOverlayLoader.value = false
        successHandler(res.data)

        if (!modalPayload.value.state) {
            if (
                (!getMobileAppState.value && modalPayload.value.id === 1) ||
                (getMobileAppState.value && modalPayload.value.id === 2)
            ) {
                showOverlayLoader.value = true
                forceLogout()
            }
        }
    } catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error.response, confirmChannelUpdateHandler)
    }
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

const successHandler = (data) => {
    const messageToast = data.message

    feedbackToastData.value = sucessObjectConstrutor(messageToast)
	showFeedbackToast.value = true
}

const errorHandler = (data, retryFn) => {
    showOverlayLoader.value = false
    showLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

initialRequests()

onBeforeUnmount(() => {
	clearFeatureStates()
})
</script>

<style lang="scss" scoped>
</style>
